import { FC } from "react";
import { Box, Alert, CircularProgress } from "@mui/material";
import { Route, Routes, useParams } from "react-router-dom";
import { DepartmentProgress } from "../../../components/lifecycle/departmentProgress";
import {
  Department,
  getStatus
} from "../../../components/lifecycle/departmentProgress/utils";
import { CommentsCard } from "../../../components/lifecycle/commentsCard";
import { InfoCard } from "./infoCard";
import {
  useSpecificCountry,
  useUpdateCountry
} from "../dataHooks/countryQueries";
import { CommentDto, Country } from "../../../api/Api";
import {
  canAccessCountryLifecycle,
  canCommentCountry,
  isEntityVpEmail
} from "../../../auth/permissions";
import { EntityVpProvider, useIsEntityVp } from "../../../auth/entityVpContext";
import { LIFECYCLE_LABELS, LifecycleName } from "../../../auth/roles";
import { useRoles } from "../../../auth/useRoles";
import { getCurrentCommentAuthor } from "../../../utils/commentUtils";
import { RequireCountryLifecycleRole } from "../../../auth/RequireCountryLifecycleRole";
import {
  MarketAccess,
  Compliance,
  Legal,
  Risk,
  CreditRisk,
  Treasury,
  Finance,
  Tech
} from "./mftTasks";

type Params = {
  id?: string;
};

/*
 * Route path, the role that guards it, and the dialog. Kept as data rather than eight near-identical
 * JSX blocks: countries have two more lifecycles than brokers, and the repetition was already the
 * least readable part of those pages.
 */
const LIFECYCLE_ROUTES: {
  path: string;
  lifecycle: LifecycleName;
  Dialog: FC<{ specificCountry: Country }>;
}[] = [
  { path: "marketaccess", lifecycle: "marketaccess", Dialog: MarketAccess },
  { path: "compliance", lifecycle: "compliance", Dialog: Compliance },
  { path: "legal", lifecycle: "legal", Dialog: Legal },
  { path: "risk", lifecycle: "risk", Dialog: Risk },
  { path: "creditrisk", lifecycle: "creditrisk", Dialog: CreditRisk },
  { path: "treasury", lifecycle: "treasury", Dialog: Treasury },
  { path: "finance", lifecycle: "finance", Dialog: Finance },
  { path: "tech", lifecycle: "tech", Dialog: Tech }
];

/*
 * Country detail page. The eight lifecycle dialogs render as routes over it, the same way the
 * counterparty and broker pages do.
 */
const CountryDetail: FC<{ id?: string }> = ({ id }) => {
  const roles = useRoles();
  const isEntityVp = useIsEntityVp();
  const canComment = canCommentCountry(roles);

  const { data: country, isLoading, isFetching } = useSpecificCountry(id);
  const updateCountry = useUpdateCountry();

  const handleAddComment = (comment: CommentDto) => {
    if (!country || !canComment) return;
    updateCountry.mutate({
      ...country,
      comments: [...(country?.comments || []), comment]
    });
  };

  const handleDeleteComment = (timestamp: string) => {
    if (!country || !canComment) return;
    updateCountry.mutate({
      ...country,
      comments: (country.comments || []).filter(
        (c) => c?.timestamp !== timestamp
      )
    });
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (country === undefined) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Country addition not found</Alert>
      </Box>
    );
  }

  const departments: Department[] = [
    {
      key: "marketaccess",
      label: LIFECYCLE_LABELS.marketaccess,
      status: getStatus(country.progress?.marketAccess)
    },
    {
      key: "compliance",
      label: LIFECYCLE_LABELS.compliance,
      status: getStatus(country.progress?.compliance)
    },
    {
      key: "creditrisk",
      label: LIFECYCLE_LABELS.creditrisk,
      status: getStatus(country.progress?.creditRisk)
    },
    {
      key: "legal",
      label: LIFECYCLE_LABELS.legal,
      status: getStatus(country.progress?.legal)
    },
    {
      key: "risk",
      label: LIFECYCLE_LABELS.risk,
      status: getStatus(country.progress?.risk)
    },

    {
      key: "treasury",
      label: LIFECYCLE_LABELS.treasury,
      status: getStatus(country.progress?.treasury)
    },
    {
      key: "finance",
      label: LIFECYCLE_LABELS.finance,
      status: getStatus(country.progress?.finance)
    },
    {
      key: "tech",
      label: LIFECYCLE_LABELS.tech,
      status: getStatus(country.progress?.tech)
    }
  ];

  return (
    <Box flex={1} sx={{ overflowY: "auto" }}>
      <InfoCard specificCountry={country} />
      <DepartmentProgress
        departments={departments}
        canAccess={(lifecycle) =>
          canAccessCountryLifecycle(roles, lifecycle, isEntityVp)
        }
      />
      <CommentsCard
        onAddComment={canComment ? handleAddComment : undefined}
        onDeleteComment={canComment ? handleDeleteComment : undefined}
        readOnly={!canComment}
        comments={(country?.comments || []).filter(
          (comment): comment is CommentDto => Boolean(comment)
        )}
      />
      <Routes>
        {LIFECYCLE_ROUTES.map(({ path, lifecycle, Dialog }) => (
          <Route
            key={path}
            path={path}
            element={
              <RequireCountryLifecycleRole lifecycle={lifecycle}>
                <Dialog specificCountry={country} key={country?.id} />
              </RequireCountryLifecycleRole>
            }
          />
        ))}
      </Routes>
      {isFetching && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000
          }}
        >
          <CircularProgress size={72} />
        </Box>
      )}
    </Box>
  );
};

export const CountryPage: FC = () => {
  const { id } = useParams<Params>();
  const { data: country } = useSpecificCountry(id);

  // A VP holds no app role, so lifecycle access for them is decided per country from the vp field
  // rather than from the token.
  const isEntityVp = isEntityVpEmail(country?.vp, getCurrentCommentAuthor());

  return (
    <EntityVpProvider value={isEntityVp}>
      <CountryDetail id={id} />
    </EntityVpProvider>
  );
};
